<template>
  <div class="flex flex-col w-[320px] p-4">
    <div class="flex items-center justify-between mb-4">
      <span class="text-sm text_color font-semibold">Edit Property</span>
    </div>

    <SkeletonLoader v-if="isLoading" page="manage_columns" />

    <div v-else class="flex flex-col gap-4">
      <div>
        <label class="block text-sm mb-2 secondary_color text-[13px]">
          Choose property
        </label>
        <a-select
          v-model:value="selectedSourceId"
          class="w-full"
          show-search
          placeholder="Select property"
          :disabled="!!originalSourceId"
          @change="handleSourceChange"
        >
          <template #dropdownRender="{ menuNode }">
            <div>
              <div class="px-3 py-2 border_bottom">
                <span class="secondary_color text-xs font-semibold">
                  {{ entityName.toUpperCase() }} PROPERTIES
                </span>
              </div>
              <component :is="menuNode" />
            </div>
          </template>
          <a-select-option
            v-for="attr in selectAttributes"
            :key="attr.id"
            :value="attr.id"
          >
            {{ attr.name }}
          </a-select-option>
        </a-select>
        <span v-if="hasSourceProperty" class="text-xs secondary_color mt-2 block">
          Source property cannot be changed after creation
        </span>
      </div>

      <div class="flex justify-end gap-2 mt-2">
        <a-button class="secondary_medium" @click.stop="handleCancel">
          {{ hasSourceProperty ? 'Close' : 'Cancel' }}
        </a-button>
        <a-button
          v-if="!hasSourceProperty"
          class="primary_medium"
          :loading="isSaving"
          :disabled="!selectedSourceId"
          @click.stop="handleSave"
        >
          Save
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import { useStore } from "vuex";
import CRMService from "@/src/api/CRMService.js";
import TicketsService from "@/src/api/TicketsService.js";
import ProjectService from "@/src/api/ProjectService.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { excludedFormFields } from "@/src/constants/crmConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";

const props = defineProps({
  entityType: {
    type: String,
    required: true,
  },
  propertyMeta: {
    type: Object,
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  service: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["cancel", "saved"]);

const store = useStore();
const isLoading = ref(true);
const isSaving = ref(false);
const selectedSourceId = ref("");
const originalSourceId = ref("");

const isTickets = computed(() => props.entityType === "tickets");
const isTasks = computed(() => props.entityType === "tasks");

const hasSourceProperty = computed(() => !!originalSourceId.value);

const entityName = computed(() => {
  const schema = store.getters["schema/getSchemaBySlug"](
    props.entityType,
    isTasks.value ? props.projectId : null,
    props.service,
  );
  return schema?.object?.name || props.entityType;
});

const selectAttributes = computed(() => {
  const schema = store.getters["schema/getSchemaBySlug"](
    props.entityType,
    isTasks.value ? props.projectId : null,
    props.service,
  );
  if (!schema?.attributes) return [];
  return schema.attributes.filter(
    (attr) => (attr.type === "select" || attr.type === "id" || attr.type === "ids") && !excludedFormFields.includes(attr.slug)
  );
});

const hasChanged = computed(() => {
  return selectedSourceId.value !== originalSourceId.value;
});

const loadCurrentMetadata = async () => {
  try {
    isLoading.value = true;

    // First check if metadata is already available in propertyMeta
    if (props.propertyMeta?.metadata?.source_attribute_id) {
      selectedSourceId.value = props.propertyMeta.metadata.source_attribute_id;
      originalSourceId.value = props.propertyMeta.metadata.source_attribute_id;
      isLoading.value = false;
      return;
    }

    // If not available, fetch from API
    let response;
    if (isTickets.value) {
      response = await TicketsService.getSchemaAttributeDetails(
        props.propertyMeta.slug
      );
    } else if (isTasks.value) {
      if (!props.projectId) {
        message.error("Project ID is required for task operations");
        return;
      }
      response = await ProjectService.tasks.getTaskAttributeDetails(
        props.projectId,
        props.propertyMeta.slug
      );
    } else {
      response = await CRMService.getPraticularAttributeDetails(
        props.entityType,
        props.propertyMeta.slug,
        props.service,
      );
    }

    if (response.success) {
      const attribute = response.attribute || response;
      if (attribute.metadata?.source_attribute_id) {
        selectedSourceId.value = attribute.metadata.source_attribute_id;
        originalSourceId.value = attribute.metadata.source_attribute_id;
      }
    }
  } catch (error) {
    console.error("Error loading select_timestamp metadata:", error);
    message.error("Failed to load property details");
  } finally {
    isLoading.value = false;
  }
};

const handleSourceChange = (value) => {
  selectedSourceId.value = value;
};

const handleCancel = () => {
  emit("cancel");
};

const handleSave = async () => {
  if (!selectedSourceId.value) {
    message.error("Please select a source property");
    return;
  }

  try {
    isSaving.value = true;

    const payload = {
      ...props.propertyMeta,
      name: props.propertyMeta.name,
      type: props.propertyMeta.type || props.propertyMeta.datatype,
      created_by_id: Number(localStorage.getItem("userId")) || 1,
      metadata: {
        source_attribute_id: selectedSourceId.value,
      },
    };

    let response;
    if (isTickets.value) {
      response = await TicketsService.editSchemaAttribute(
        props.propertyMeta.slug,
        payload
      );
    } else if (isTasks.value) {
      if (!props.projectId) {
        message.error("Project ID is required for task operations");
        return;
      }
      response = await ProjectService.tasks.editTaskSchemaAttribute(
        props.projectId,
        props.propertyMeta.slug,
        payload
      );
    } else {
      response = await CRMService.updateSchemaObjectAttribute(
        props.entityType,
        props.propertyMeta.slug,
        payload,
        props.service,
      );
    }

    if (response.success) {
      const entitySlug = isTickets.value
        ? entityTypes.TICKETS
        : isTasks.value
          ? entityTypes.TASKS
          : props.entityType;
      await store.dispatch("schema/refreshSchema", {
        slug: entitySlug,
        projectId: isTasks.value ? props.projectId : null,
        service: props.service || null,
      });

      message.success("Property updated successfully");
      emit("saved", { response });
    } else {
      let errorMsg = "Failed to update property";
      if (response.errors?.length > 0) {
        errorMsg = response.errors[0];
      } else if (response.error) {
        errorMsg = response.error;
      } else if (response.message) {
        errorMsg = response.message;
      }
      message.error(errorMsg);
    }
  } catch (error) {
    console.error("Error updating select_timestamp property:", error);
    message.error("Failed to update property");
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  loadCurrentMetadata();
});
</script>

